import { Maybe } from '../lib/MaybeV2';
import React, { ReactNode } from 'react';

export type ClickEvent = React.MouseEvent<HTMLElement>;

function execCallback<T extends keyof Props>(
    props: Props,
    propName: T,
    e: ClickEvent,
): void {
    if (!props.disabled && props[propName]) {
        (props[propName] as (e: ClickEvent) => void)(e);
    }
}

export interface ClickHandler {
    (e?: ClickEvent): void;
}

interface Props extends ClickableDivProps {
    element: string;
    children?: ReactNode;
}

export interface ClickableDivProps {
    disabled?: boolean;
    className?: string;
    innerRef?: React.Ref<HTMLElement>;
    onClick?: Maybe<ClickHandler>;
    onClickStart?: Maybe<ClickHandler>;
    onClickEnd?: Maybe<ClickHandler>;
    onMouseDown?: void;
    onTouchStart?: void;
    onTouchEnd?: void;
    onMouseOver?: Maybe<React.MouseEventHandler<HTMLElement>>;
    onMouseOut?: Maybe<React.MouseEventHandler<HTMLElement>>;
    onMouseEnter?: Maybe<React.MouseEventHandler<HTMLElement>>;
    onMouseLeave?: Maybe<React.MouseEventHandler<HTMLElement>>;
    target?: string;
    href?: string;
    style?: React.CSSProperties;
    type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
}

export function Clickable(props: Props) {
    const style: React.CSSProperties = Object.assign(
        !props.disabled ? { cursor: 'pointer' } : {},
        props.style,
    );
    const domProps: Props = Object.assign({}, props);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    delete domProps.element;
    delete domProps.onClickStart;
    delete domProps.onClickEnd;
    delete domProps.disabled;
    delete domProps.innerRef;

    return React.createElement(
        props.element,
        Object.assign({}, domProps, {
            onClick: (e: ClickEvent): void => execCallback(props, 'onClick', e),
            onMouseUp: (e: ClickEvent): void =>
                execCallback(props, 'onClickEnd', e),
            onMouseDown: (e: ClickEvent): void =>
                execCallback(props, 'onClickStart', e),
            onTouchStart: (e: ClickEvent): void =>
                execCallback(props, 'onClickStart', e),
            onTouchEnd: (e: ClickEvent): void =>
                execCallback(props, 'onClickEnd', e),
            onMouseOver: (e: ClickEvent): void =>
                execCallback(props, 'onMouseOver', e),
            onMouseOut: (e: ClickEvent): void =>
                execCallback(props, 'onMouseOut', e),
            onMouseEnter: (e: ClickEvent): void =>
                execCallback(props, 'onMouseEnter', e),
            onMouseLeave: (e: ClickEvent): void =>
                execCallback(props, 'onMouseLeave', e),
            'data-disabled': props.disabled || false,
            tabIndex: -1,
            style: style,
            ref: props.innerRef,
            type: props.element === 'button' ? props.type : undefined,
        }),
        props.children,
    );
}

export const ClickableFactory = (
    props: Props,
    ...children: ReactNode[]
): React.ReactElement => React.createElement(Clickable, props, ...children);
